// src/components/sdk/components/UsageLogTable.tsx
import React from 'react';
import { Table, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { UsageLogData, UsageLogTableProps, UsageLogTableState } from '../SdkInterface';

interface ExtendedUsageLogTableProps extends UsageLogTableProps {
    isMobile?: boolean;
    panelExpanded?: boolean;
}

export class UsageLogTable extends React.Component<ExtendedUsageLogTableProps, UsageLogTableState> {
    constructor(props: ExtendedUsageLogTableProps) {
        super(props);
        this.state = {};
    }

    // 获取日志类型对应的文本和颜色
    getLogTypeInfo = (type: number, t: (key: string) => string): { text: string; color: string } => {
        const typeMap: Record<number, { key: string; color: string; defaultText: string }> = {
            1: { key: 'sdk.usage.query', color: 'orange', defaultText: '查询' },
            2: { key: 'sdk.usage.use', color: 'green', defaultText: '使用' },
        };

        const info = typeMap[type];
        if (info) {
            return {
                text: t(info.key) || info.defaultText,
                color: info.color,
            };
        }
        return {
            text: t('sdk.usage.unknown') || `未知类型(${type})`,
            color: 'gray',
        };
    };

    // 获取使用日志表格列配置
    getUsageColumns = (): ColumnsType<UsageLogData> => {
        const { t, isMobile } = this.props;

        // 移动端：只显示 ID、日志类型、应用名称、使用时间 四列
        if (isMobile) {
            return [
                {
                    title: t('sdk.usage.id') || 'ID',
                    dataIndex: 'id',
                    key: 'id',
                    width: 45,
                    fixed: 'left',
                },
                {
                    title: t('sdk.usage.type') || '日志类型',
                    dataIndex: 'type',
                    key: 'type',
                    width: 70,
                    render: (type: number) => {
                        const info = this.getLogTypeInfo(type, t);
                        return <Tag color={info.color} style={{ fontSize: 11, margin: 0 }}>{info.text}</Tag>;
                    },
                },
                {
                    title: t('sdk.usage.appName') || '应用名称',
                    dataIndex: 'appName',
                    key: 'appName',
                    width: 80,
                    ellipsis: true,
                },
                {
                    title: t('sdk.usage.usageTime') || '使用时间',
                    dataIndex: 'usageTime',
                    key: 'usageTime',
                    width: 100,
                },
            ];
        }

        // 桌面端：显示所有列
        return [
            {
                title: t('sdk.usage.id') || 'ID',
                dataIndex: 'id',
                key: 'id',
                width: 60,
            },
            {
                title: t('sdk.usage.type') || '日志类型',
                dataIndex: 'type',
                key: 'type',
                width: 100,
                render: (type: number) => {
                    const info = this.getLogTypeInfo(type, t);
                    return <Tag color={info.color}>{info.text}</Tag>;
                },
            },
            {
                title: t('sdk.usage.appName') || '应用名称',
                dataIndex: 'appName',
                key: 'appName',
                width: 120,
            },
            {
                title: t('sdk.usage.packageName') || '包名',
                dataIndex: 'packageName',
                key: 'packageName',
                width: 120,
                ellipsis: true,
            },
            {
                title: t('sdk.usage.usageTime') || '使用时间',
                dataIndex: 'usageTime',
                key: 'usageTime',
                width: 160,
            },
            {
                title: t('sdk.usage.deviceType') || '设备类型',
                dataIndex: 'deviceType',
                key: 'deviceType',
                width: 100,
            },
            {
                title: t('sdk.usage.mac') || 'MAC地址',
                dataIndex: 'mac',
                key: 'mac',
                width: 140,
                ellipsis: true,
            },
        ];
    };

    render() {
        const {
            t,
            usageLogs,
            usageLoading,
            usageTotal,
            usagePage,
            usagePageSize,
            onPageChange,
            isMobile = false,
            panelExpanded = false,
        } = this.props;

        // 根据面板展开状态计算表格高度
        const getTableHeight = () => {
            if (!isMobile) return 'auto';
            // 面板展开时，表格区域变小；面板收起时，表格区域变大
            const baseOffset = panelExpanded ? 550 : 330;
            return `calc(100vh - ${baseOffset}px)`;
        };

        const getContainerHeight = () => {
            if (!isMobile) return 'auto';
            const baseOffset = panelExpanded ? 430 : 250;
            return `calc(100vh - ${baseOffset}px)`;
        };

        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                height: isMobile ? getContainerHeight() : 'auto',
                minHeight: isMobile ? 300 : 'auto',
            }}>
                <Table
                    columns={this.getUsageColumns()}
                    dataSource={usageLogs}
                    loading={usageLoading}
                    pagination={{
                        total: usageTotal,
                        current: usagePage,
                        pageSize: usagePageSize,
                        showTotal: (total) => `${t('sdk.total') || '共'} ${total} ${t('sdk.usage.records') || '条记录'}`,
                        showSizeChanger: !isMobile,
                        pageSizeOptions: ['10', '20', '50'],
                        onChange: onPageChange,
                        size: isMobile ? 'small' : 'default',
                        position: isMobile ? ['bottomCenter'] : ['bottomRight'],
                    }}
                    rowKey="id"
                    size={isMobile ? 'small' : 'middle'}
                    scroll={isMobile ? { x: 300, y: getTableHeight() } : undefined}
                    style={isMobile ? {
                        flex: 1,
                        overflow: 'hidden',
                    } : {}}
                />
            </div>
        );
    }
}

export default UsageLogTable;